import React, { useRef, useState } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_location_rack_wh_in.css";
import axios from "axios";
import useSWR from "swr";


export interface LocationDataType {
    key: string;
    locationCode: string;
    locationName: string;
}

type DataIndex = keyof LocationDataType;
const fetcher = (url: string) => axios.get(url).then((res) => res.data);


interface ModalLocationWHinProps {
    onRowDoubleClick: (record: LocationDataType) => void;
    warehouseCodeTo?: string;
}


const ModalLocationWHin: React.FC<ModalLocationWHinProps> = ({ onRowDoubleClick, warehouseCodeTo }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const { data, error } = useSWR(
        warehouseCodeTo ? `${process.env.REACT_APP_BACKEND_URL}/api/location-rack/${warehouseCodeTo}` : null,
        fetcher
    );

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<LocationDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<LocationDataType> = [
        {
            title: 'Location Code',
            dataIndex: 'locationCode',
            key: 'locationCode',
            width: '30%',
            ...getColumnSearchProps('locationCode'),
            sorter: (a, b) => a.locationCode.localeCompare(b.locationCode),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Location Name',
            dataIndex: 'locationName',
            key: 'locationName',
            ...getColumnSearchProps('locationName'),
            sorter: (a, b) => a.locationName.localeCompare(b.locationName),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    if (error) return <div>Failed to load data.</div>;

    const tableData: LocationDataType[] = data?.map((item: any) => ({
        key: item.code_rack,
        locationCode: item.code_rack,
        locationName: item.name_rack,
    })) || [];

    return (
        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="i4qf">
                <div className="gjs-grid-column" id="i7yb">
                    <Table<LocationDataType>
                        columns={columns}
                        dataSource={tableData}
                        loading={!data && !error && !!warehouseCodeTo}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    onRowDoubleClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalLocationWHin;